import { uploadFileApi } from "@/api/services/global/file";
import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { FILE_STATUS, TFileUploadFor } from "@/constants/file";
import { button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { TExtendFile, TExtendFileOption } from "@/types/file";
import { showError } from "@/utils/error";
import {
  TFileRules,
  checkFileListWhetherIsActualPdf,
  convertByteToMb,
  covertMimeTypesToAccepts,
  getExtensionsOfMimeTypes,
  getFileErrorMessage,
  getFileStatusWithRules,
} from "@/utils/file";
import { toNumber } from "@/utils/number";
import { makeUuid } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography, css, styled } from "@mui/material";
import { Key, useCallback, useEffect, useState } from "react";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";

export type TDropZoneProps = {
  value?: TExtendFile | TExtendFile[] | null;
  onChange?: (value: TExtendFile | TExtendFile[] | null) => void;
  options?: Omit<DropzoneOptions, "onDrop">;
  enableFileList?: boolean;
  nonControl?: boolean;
  rules?: TFileRules;
  bordered?: boolean;
  disabled?: boolean;
  uploadFor?: TFileUploadFor;
  hiddenCloseIcon?: boolean;
  hideAfterUpload?: boolean;
  handleFileRejections?: (fileRejections: FileRejection[]) => void;
};

export const DropZone: FCC<TDropZoneProps> = ({
  options = {},
  value,
  onChange,
  enableFileList = true,
  children,
  rules = {},
  nonControl = false,
  bordered = true,
  hiddenCloseIcon = false,
  disabled,
  uploadFor,
  hideAfterUpload = false,
  handleFileRejections,
}) => {
  const { field } = useFormField(nonControl);
  const [files, setFiles] = useState<TExtendFile[]>([field?.value ?? value ?? []].flat());
  const [uploading, setUploading] = useState(false);
  const [hideDropZone, setHideDropZone] = useState(false);

  useEffect(() => {
    if (field?.value ?? value) {
      const newValue: TExtendFile[] = [field?.value ?? value].flat();
      if (files.some((file) => !newValue.find(({ id }) => file.id === id))) {
        setFiles([field?.value ?? value].flat());
      }
    } else {
      setFiles([]);
    }
  }, [value, field]);

  useEffect(() => {
    if (hideAfterUpload && files.length > 0) {
      setHideDropZone(true);
      return;
    }

    setHideDropZone(false);
  }, [files]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.some((file) => file.size === 0)) {
        toast.error("0Byteのファイルはアップロードできません。");
        return;
      }

      if (
        !!rules.acceptMimeTypes &&
        fileRejections.some((file) => !rules.acceptMimeTypes?.includes(file.file.name.includes(".rar") ? "application/vnd.rar" : file.file.type))
      ) {
        toast.error(`アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []).join("、")}です。`);
        return;
      }

      if (
        !!rules.acceptMimeTypes &&
        acceptedFiles.some((file) => !rules.acceptMimeTypes?.includes(file.name.includes(".rar") ? "application/vnd.rar" : file.type))
      ) {
        toast.error(`アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []).join("、")}です。`);
        return;
      }

      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }

      if (rules?.maxFile && acceptedFiles.length + files.length > rules?.maxFile) {
        toast.error(`ファイルは最大${rules?.maxFile}件までアップロードできます。`);
        return;
      }

      if (rules?.maxSize && acceptedFiles.some((file) => toNumber(file.size) > toNumber(rules?.maxSize))) {
        toast.error(`ファイルサイズは${convertByteToMb(rules.maxSize)}MB以内でお願いします。`);
        return;
      }

      if (fileRejections.length > 0 && handleFileRejections) {
        handleFileRejections(fileRejections);
        return;
      }
      let newFiles = files;
      setUploading(true);
      const droppedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const fileOption: TExtendFileOption = {
            id: makeUuid(),
            status: getFileStatusWithRules(file, rules),
          };

          if (fileOption.status === FILE_STATUS.OK && uploadFor) {
            try {
              const rs = await uploadFileApi({ file, uploadFor });
              return Object.assign(file, { ...fileOption, filePath: rs.data.filePath });
            } catch (error) {
              showError(error);
              return Object.assign(file, { ...fileOption, status: FILE_STATUS.ERROR });
            }
          }
          return Object.assign(file, fileOption);
        }),
      );

      if (options.multiple) {
        newFiles = [newFiles, droppedFiles].flat();
      } else {
        newFiles = [droppedFiles[0]];
      }
      setFiles(newFiles);
      triggerChangeFiles(newFiles);
      setUploading(false);
    },
    [uploadFor, files],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...options,
    onDrop,
    accept: rules.dropzoneAccept || covertMimeTypesToAccepts(rules.acceptMimeTypes),
    disabled: uploading || disabled,
  });

  const removeFile = (removedId: Key) => {
    const updatedFiles = files.filter(({ id }) => removedId !== id);
    setFiles(updatedFiles);
    triggerChangeFiles(updatedFiles);
    onChange?.(updatedFiles);
    triggerChangeFiles(updatedFiles);
  };

  const triggerChangeFiles = (files: TExtendFile[]) => {
    const { multiple } = options;
    onChange?.(multiple === false ? files.at(0) ?? null : files);
    field?.onChange(multiple === false ? files.at(0) ?? null : files);
    field?.onBlur();
  };

  return (
    <Box>
      <Box {...getRootProps()}>
        <input {...getInputProps()} />
        {!hideDropZone && (
          <DropZoneArea dragged={isDragActive} bordered={bordered}>
            {children ?? (
              <>
                <Typography variant="body14">アップロードするファイルをドラッグ&ドロップ</Typography>
                <Typography variant="body14">または</Typography>
                <Button variant="outline">ファイルを選択する</Button>
              </>
            )}
            {uploading && <UploadingBackup>作成中…</UploadingBackup>}
          </DropZoneArea>
        )}
      </Box>
      {enableFileList && (
        <Stack flexDirection="row" flexWrap="wrap" gap={1} mt={files.length && !hideDropZone ? 1 : 0}>
          {files.map((file) => {
            const fileProps = file.filePath ? { filePath: file.filePath } : { file };
            return (
              <FileItem
                key={file.id}
                {...fileProps}
                downloadable={true}
                onRemove={!hiddenCloseIcon ? () => removeFile(file.id) : undefined}
                maxWidth={336}
                errorMsg={getFileErrorMessage(file, rules)}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const options = { shouldForwardProp: (propName: string) => !["dragged", "bordered"].includes(propName) };

const DropZoneAreaActive = css`
  border-color: ${button.primary};
`;

const DropZoneArea = styled(Stack, options)<{ dragged: boolean; bordered: boolean }>`
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 16px;
  border: 1px dashed ${divider.middle};
  border-radius: ${rounded.sm};
  overflow: hidden;

  ${({ dragged }) => dragged && DropZoneAreaActive}
  ${({ bordered }) =>
    !bordered &&
    css`
      padding: 0;
      border: none;
    `}
`;

const UploadingBackup = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: ${button.disable};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${text.white};
`;
